import { ampli } from '@/ampli';
import type { Key, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import type { Chain } from 'viem';

import { logger } from '@/lib/logger';

import {
  CHAIN_IDS,
  NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID,
  SUPPORTED_CHAIN_IDS,
} from '@/constants/chain';

import { useTokenResultsMultiChain } from '@/hooks/tokenQueries/useTokenResults';
import { useBalances } from '@/hooks/useBalances';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useSearchTokens } from '@/hooks/useSearchTokens';
import { useTrendingTokens } from '@/hooks/useTrendingTokens';

import { recentlyViewedTokensSelectors, useSearchStore } from '@/store/search';

import type { TokenBase, TokenResult, TokensResponse } from '@/utils/0x/token-registry.types';
import { SEARCH_TYPES } from '@/utils/amplitude';
import type { ChainAddress } from '@/utils/models';
import { isChainAddress } from '@/utils/models';
import { tokenKey } from '@/utils/normalizeAddress';
import { joinSearchResults } from '@/utils/search';
import {
  searchTrendWalletsKey,
  type SearchReceiptContext,
  type SearchTokensPage,
} from '@/utils/search-trends';

import {
  SEARCH_SECTION_TYPE_BALANCES,
  SEARCH_SECTION_TYPE_RESULTS,
  SEARCH_SECTION_TYPE_TRENDING_TOKENS,
} from './TokenSelectModal/SearchComboBox/getSections';
import {
  mapBalanceItemToTokenResult,
  mergeSearchResultsWithBalances,
  type TokenWithBalance,
} from './TokenSelectModal/utils';
import {
  buildItemIndex,
  buildTokenSelectSections,
  getDisabledState,
  prepareRecentTokens,
  type TokenSelectItem,
  type TokenSelectSection,
} from './tokenSelectSections';
import type { SelectTokenFilter } from './types';

const defaultPaginatedResults: SearchTokensPage[] = [];

/** Owns token-picker data, section assembly, selection, and search analytics. */
export function useTokenSelect({
  chainId,
  supportedChains,
  isOpen,
  searchType,
  filterToken,
  onSelect,
}: {
  chainId?: number;
  supportedChains?: Chain[];
  isOpen: boolean;
  searchType: SEARCH_TYPES;
  filterToken?: SelectTokenFilter;
  onSelect: (token: TokenBase, searchReceiptId?: string) => void;
}): {
  inputValue: string;
  onInputChange: (value: string) => void;
  selectedChainId: number | undefined;
  onChangeChainFilter: (chainId: number | undefined) => void;
  suggestedTokensChainId: number;
  sections: TokenSelectSection[];
  disabledKeys: string[] | undefined;
  disabledReasons: Map<string, ReactNode> | undefined;
  resolveItem: (key: Key) => TokenSelectItem | undefined;
  handleSelectionChange: (key: Key | null) => void;
  selectTokenResult: (token: TokenBase) => void;
  clearRecentlyViewed: () => void;
  isSearching: boolean;
  isLoadingTrending: boolean;
  hasError: boolean;
  noResults: boolean;
} {
  const { solanaWallet, ethereumWallet, walletsReady, isConnected } = useMatchaWallets();
  const solanaWalletAddress = solanaWallet?.address;
  const ethereumWalletAddress = ethereumWallet?.address;
  const [selectedChainId, setSelectedChainId] = useState(chainId);
  const [showAllBalances, setShowAllBalances] = useState(false);
  const [showAllTrending, setShowAllTrending] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue, setDebouncedInputValue] = useState('');
  const hasSelectedTokenRef = useRef(false);
  const lastKeywordSearchRef = useRef<{ query: string; results: TokenBase[] } | null>(null);
  const previousIsOpenRef = useRef(isOpen);

  useDebounce(() => setDebouncedInputValue(inputValue), 300, [inputValue]);

  const wallets = useMemo(() => {
    const addresses: ChainAddress[] = [];
    if (ethereumWalletAddress) addresses.push(ethereumWalletAddress as ChainAddress);
    if (solanaWalletAddress) addresses.push(solanaWalletAddress as ChainAddress);
    return addresses;
  }, [ethereumWalletAddress, solanaWalletAddress]);
  const trend = useMemo(
    () =>
      isOpen &&
      walletsReady &&
      isConnected &&
      (searchType === SEARCH_TYPES.HEADER || searchType === SEARCH_TYPES.HOME_PAGE) &&
      (ethereumWalletAddress || solanaWalletAddress)
        ? {
            wallets: {
              ethereum: ethereumWalletAddress?.toLowerCase(),
              solana: solanaWalletAddress,
            },
            source: searchType,
          }
        : undefined,
    [isOpen, walletsReady, isConnected, searchType, ethereumWalletAddress, solanaWalletAddress],
  );
  const trendWalletsKey = trend ? searchTrendWalletsKey(trend.wallets) : undefined;

  useEffect(() => {
    if (isOpen) {
      hasSelectedTokenRef.current = false;
      lastKeywordSearchRef.current = null;
    }
  }, [isOpen]);

  const supportedEthereumChainId = supportedChains?.find(
    (chain) => chain.id === CHAIN_IDS.MAINNET,
  )?.id;
  const suggestedTokensChainId =
    selectedChainId ?? supportedEthereumChainId ?? supportedChains?.[0]?.id ?? CHAIN_IDS.MAINNET;

  const chainIds = useMemo(
    () =>
      selectedChainId
        ? [selectedChainId]
        : supportedChains
          ? supportedChains.map((chain) => chain.id)
          : SUPPORTED_CHAIN_IDS,
    [selectedChainId, supportedChains],
  );

  const isAddressSearch = isChainAddress(debouncedInputValue);
  const useUntrackedAddressLookup = isAddressSearch && !trend;
  const {
    data: searchInfiniteData,
    isLoading: isLoadingQueryResults,
    isFetching: isValidatingQueryResults,
    error: queryError,
    fetchNextPage: fetchNextSearchPage,
  } = useSearchTokens({
    chainIds,
    limit: 30,
    query: useUntrackedAddressLookup ? undefined : debouncedInputValue,
    enabled: isOpen,
    trend,
  });
  const { tokenMap: addressTokenMap, isLoading: isLoadingAddressResults } =
    useTokenResultsMultiChain(
      isOpen && useUntrackedAddressLookup ? [debouncedInputValue] : undefined,
      isOpen && useUntrackedAddressLookup ? chainIds : undefined,
    );

  const searchResultsPaginated = useMemo<SearchTokensPage[] | undefined>(() => {
    if (useUntrackedAddressLookup) {
      if (!addressTokenMap) return undefined;
      const tokens = Object.values(addressTokenMap).flatMap((chainTokens) =>
        Object.values(chainTokens).filter((token): token is TokenResult => !!token),
      );
      return [{ data: tokens, resultCount: tokens.length }] satisfies TokensResponse[];
    }
    return searchInfiniteData?.pages;
  }, [useUntrackedAddressLookup, addressTokenMap, searchInfiniteData]);

  const isLoadingSearchResults = useUntrackedAddressLookup
    ? isLoadingAddressResults
    : isLoadingQueryResults;
  const isValidatingSearch = useUntrackedAddressLookup
    ? isLoadingAddressResults
    : isValidatingQueryResults;
  const searchError = useUntrackedAddressLookup ? null : queryError;
  const searchResults = useMemo(
    () => (searchResultsPaginated ? joinSearchResults(searchResultsPaginated) : undefined),
    [searchResultsPaginated],
  );

  const searchTokensFilter = useMemo(() => {
    if (!searchResults || wallets.length === 0) return undefined;
    return searchResults.map((token) => ({
      address: token.address as ChainAddress,
      chainId: token.chainId,
    }));
  }, [searchResults, wallets]);
  const { data: searchResultsBalances } = useBalances({
    wallets,
    tokens: searchTokensFilter,
  });
  const searchResultsWithBalances = useMemo(
    () =>
      searchResults
        ? mergeSearchResultsWithBalances(searchResults, searchResultsBalances)
        : undefined,
    [searchResults, searchResultsBalances],
  );

  useEffect(() => {
    if (searchResults && debouncedInputValue) {
      ampli.searchExecuted({
        search_query: debouncedInputValue,
        search_results_count: searchResults.length,
        search_type: searchType,
      });
    }
  }, [debouncedInputValue, searchResults, searchType]);

  useEffect(() => {
    if (
      !isOpen ||
      !debouncedInputValue ||
      isChainAddress(debouncedInputValue) ||
      !searchResults?.length
    ) {
      return;
    }
    lastKeywordSearchRef.current = { query: debouncedInputValue, results: searchResults };
  }, [debouncedInputValue, isOpen, searchResults]);

  const {
    data: trendingTokens,
    isLoading: isLoadingTrending,
    error: trendingResultsError,
  } = useTrendingTokens({ chainIds, limit: 30 });
  const { data: balancesData } = useBalances({
    wallets,
    chainIds: selectedChainId ? [selectedChainId] : undefined,
  });
  const tokensWithBalances = useMemo(() => {
    if (!balancesData) return undefined;
    return balancesData
      .map(mapBalanceItemToTokenResult)
      .filter((token): token is TokenWithBalance => token !== null);
  }, [balancesData]);
  const balanceLookup = useMemo(() => {
    if (!balancesData) return undefined;
    const lookup = new Map<string, { balance: string; balanceUsd: string }>();
    for (const item of balancesData) {
      if (!item.balanceUsd) continue;
      lookup.set(tokenKey(item.tokenAddress, item.networkId), {
        balance: item.shiftedBalance.toString(),
        balanceUsd: item.balanceUsd,
      });
    }
    return lookup;
  }, [balancesData]);

  const { recentlyViewed, addRecentlyViewedToken, clearRecentlyViewedTokens } = useSearchStore(
    recentlyViewedTokensSelectors,
  );
  const recentTokens = useMemo(
    () =>
      prepareRecentTokens({
        recentlyViewed,
        selectedChainId,
        searchResults: searchResultsWithBalances,
        balanceLookup,
      }),
    [recentlyViewed, selectedChainId, searchResultsWithBalances, balanceLookup],
  );

  const paginatedResults = searchResultsPaginated ?? defaultPaginatedResults;
  const hasMoreSearchResults =
    !isAddressSearch &&
    !!searchResults &&
    paginatedResults.length > 0 &&
    'resultCount' in paginatedResults[0] &&
    searchResults.length < paginatedResults[0].resultCount;
  const isLoadingMoreSearchResults = !isLoadingSearchResults && isValidatingSearch;
  const sections = useMemo(
    () =>
      buildTokenSelectSections({
        searchResults: searchResultsWithBalances,
        trendingTokens,
        tokensWithBalances,
        recentTokens,
        showAllBalances,
        showAllTrending,
        hasMoreSearchResults,
        isLoadingMoreSearchResults,
      }),
    [
      searchResultsWithBalances,
      trendingTokens,
      tokensWithBalances,
      recentTokens,
      showAllBalances,
      showAllTrending,
      hasMoreSearchResults,
      isLoadingMoreSearchResults,
    ],
  );
  const itemIndex = useMemo(() => buildItemIndex(sections), [sections]);
  const { disabledKeys, disabledReasons } = useMemo(
    () => getDisabledState(sections, filterToken),
    [sections, filterToken],
  );

  const resolveItem = useCallback((key: Key) => itemIndex.get(key.toString()), [itemIndex]);
  const commitSelection = useCallback(
    (token: TokenBase, searchReceiptId?: string) => {
      hasSelectedTokenRef.current = true;
      addRecentlyViewedToken(token);
      if (searchReceiptId) onSelect(token, searchReceiptId);
      else onSelect(token);
    },
    [addRecentlyViewedToken, onSelect],
  );
  const selectTokenResult = useCallback(
    (token: TokenBase) => commitSelection(token),
    [commitSelection],
  );
  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      if (key === null) return;
      const item = resolveItem(key);
      if (!item) {
        logger.warn(`Could not find token info for key ${key.toString()}`);
        return;
      }
      if (item.kind === 'loadMore') {
        ampli.searchShowMoreClicked({ search_type: searchType });
        switch (item.sectionId) {
          case SEARCH_SECTION_TYPE_BALANCES:
            setShowAllBalances(true);
            break;
          case SEARCH_SECTION_TYPE_RESULTS:
            void fetchNextSearchPage();
            break;
          case SEARCH_SECTION_TYPE_TRENDING_TOKENS:
            setShowAllTrending(true);
            break;
        }
        return;
      }

      const { token } = item;
      ampli.searchTokenSelected({
        search_selected_symbol: token.symbol,
        search_selected_address: token.address,
        search_selected_chain_name: NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[token.chainId],
        search_type: searchType,
      });
      let searchReceipt: SearchReceiptContext | undefined;
      if (item.sectionId === SEARCH_SECTION_TYPE_RESULTS && trendWalletsKey) {
        const selectedTokenKey = tokenKey(token.address, token.chainId);
        for (const page of paginatedResults) {
          const receipt = page.searchReceipt;
          if (
            receipt?.walletsKey === trendWalletsKey &&
            (!searchReceipt || receipt.expiresAt >= searchReceipt.expiresAt) &&
            'data' in page &&
            page.data.some(
              (candidate) => tokenKey(candidate.address, candidate.chainId) === selectedTokenKey,
            )
          ) {
            searchReceipt = receipt;
          }
        }
      }
      commitSelection(token, searchReceipt?.id);
    },
    [
      fetchNextSearchPage,
      resolveItem,
      searchType,
      commitSelection,
      paginatedResults,
      trendWalletsKey,
    ],
  );

  useEffect(() => {
    if (
      !isOpen &&
      previousIsOpenRef.current &&
      lastKeywordSearchRef.current &&
      !hasSelectedTokenRef.current
    ) {
      ampli.searchAbandoned({ search_type: searchType });
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen, searchType]);

  useEffect(() => {
    if (chainId) setSelectedChainId(chainId);
  }, [chainId]);

  const onInputChange = useCallback((value: string) => setInputValue(value), []);
  const onChangeChainFilter = useCallback(
    (nextChainId: number | undefined) => setSelectedChainId(nextChainId),
    [],
  );

  const hasError =
    (debouncedInputValue.length > 0 && !!searchError) ||
    (debouncedInputValue.length === 0 && !!trendingResultsError);
  const noResults =
    !isLoadingSearchResults && searchResults?.length === 0 && debouncedInputValue.length > 0;

  return {
    inputValue,
    onInputChange,
    selectedChainId,
    onChangeChainFilter,
    suggestedTokensChainId,
    sections,
    disabledKeys,
    disabledReasons,
    resolveItem,
    handleSelectionChange,
    selectTokenResult,
    clearRecentlyViewed: clearRecentlyViewedTokens,
    isSearching: isLoadingSearchResults,
    isLoadingTrending,
    hasError,
    noResults,
  };
}
